import { useState } from 'react';

import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { company } from '@/data/company';
import { contactFormSchema, SERVICE_CATEGORIES } from '@/lib/contact-schema';

interface FormState {
  name: string;
  email: string;
  phone: string;
  category: (typeof SERVICE_CATEGORIES)[number];
  message: string;
  consent: boolean;
  honeypot: string;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const initialState: FormState = {
  name: '',
  email: '',
  phone: '',
  category: 'baumpflege',
  message: '',
  consent: false,
  honeypot: '',
};

const categoryLabels: Record<(typeof SERVICE_CATEGORIES)[number], string> = {
  baumpflege: 'Baumpflege',
  baumhausbau: 'Baumhausbau',
  baumfaellung: 'Baumfällung',
  notfall: 'Notfall (Sturm / Bruch)',
  sonstiges: 'Sonstiges',
};

export function ContactForm(): React.ReactNode {
  const [form, setForm] = useState<FormState>(initialState);
  const [state, setState] = useState<SubmitState>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resendDisabled, setResendDisabled] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const parsed = contactFormSchema.safeParse({
      name: form.name,
      email: form.email,
      phone: form.phone === '' ? undefined : form.phone,
      category: form.category,
      message: form.message,
      consent: form.consent ? (true as const) : undefined,
      honeypot: form.honeypot,
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setState('submitting');
    setResendDisabled(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });

      if (!response.ok) {
        setState('error');
        return;
      }
      setState('success');
      setForm(initialState);
    } catch {
      setState('error');
    } finally {
      window.setTimeout(() => {
        setResendDisabled(false);
      }, 30000);
    }
  };

  if (state === 'success') {
    return (
      <div className="rounded-lg border border-success bg-paper p-8 text-center">
        <Icon name="CircleCheck" className="mx-auto mb-4 h-12 w-12 text-success" />
        <h3 className="mb-2 text-2xl font-semibold">Vielen Dank!</h3>
        <p className="text-mute">
          Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück. Bei akutem Notfall:{' '}
          <a href={company.phone.href} className="font-medium underline">
            {company.phone.display}
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-stone bg-paper p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Name"
          name="name"
          value={form.name}
          onChange={(value) => {
            update('name', value);
          }}
          error={errors['name']}
          required
        />
        <Field
          label="E-Mail"
          name="email"
          type="email"
          value={form.email}
          onChange={(value) => {
            update('email', value);
          }}
          error={errors['email']}
          required
        />
        <Field
          label="Telefon (optional)"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={(value) => {
            update('phone', value);
          }}
          error={errors['phone']}
        />
        <div>
          <label htmlFor="category" className="mb-1 block text-sm font-medium">
            Anliegen
          </label>
          <select
            id="category"
            value={form.category}
            onChange={(event) => {
              update('category', event.target.value as (typeof SERVICE_CATEGORIES)[number]);
            }}
            className="w-full rounded-md border border-stone bg-paper px-3 py-2 focus:border-bark focus:outline-none"
          >
            {SERVICE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {categoryLabels[cat]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {form.category === 'notfall' ? (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-emergency/10 p-3 text-sm text-emergency">
          <Icon name="TriangleAlert" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Bei akutem Notfall bitte direkt anrufen:{' '}
            <a href={company.phone.href} className="font-semibold underline">
              {company.phone.display}
            </a>
          </span>
        </div>
      ) : null}

      <div className="mt-4">
        <label htmlFor="message" className="mb-1 block text-sm font-medium">
          Nachricht
        </label>
        <textarea
          id="message"
          rows={5}
          value={form.message}
          onChange={(event) => {
            update('message', event.target.value);
          }}
          aria-describedby={errors['message'] ? 'message-error' : undefined}
          className="w-full rounded-md border border-stone bg-paper px-3 py-2 focus:border-bark focus:outline-none"
        />
        {errors['message'] ? (
          <p id="message-error" className="mt-1 text-sm text-emergency">
            {errors['message']}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex items-start gap-2 text-sm">
        <input
          id="consent"
          type="checkbox"
          checked={form.consent}
          onChange={(event) => {
            update('consent', event.target.checked);
          }}
          className="mt-1 h-4 w-4 rounded border-stone"
        />
        <label htmlFor="consent" className="text-mute">
          Ich habe die{' '}
          <a href="/datenschutz" className="underline">
            Datenschutzerklärung
          </a>{' '}
          gelesen und stimme der Verarbeitung meiner Angaben zu.
        </label>
      </div>
      {errors['consent'] ? (
        <p className="mt-1 text-sm text-emergency">{errors['consent']}</p>
      ) : null}

      <div className="hidden">
        <label htmlFor="company-website" aria-hidden="true">
          Bitte freilassen
          <input
            id="company-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={(event) => {
              update('honeypot', event.target.value);
            }}
          />
        </label>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-mute">
          Wir melden uns innerhalb von 24 Stunden – meist deutlich schneller.
        </p>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          disabled={state === 'submitting' || resendDisabled}
          size="lg"
        >
          {state === 'submitting' ? 'Wird gesendet…' : 'Anfrage abschicken'}
          <Icon name="ArrowRight" className="h-4 w-4" />
        </Button>
      </div>

      {state === 'error' ? (
        <p className="mt-4 text-sm text-emergency">
          Senden hat nicht geklappt. Bitte rufen Sie uns direkt an:{' '}
          <a href={company.phone.href} className="font-medium underline">
            {company.phone.display}
          </a>
        </p>
      ) : null}
    </div>
  );
}

interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}

function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  error,
  required = false,
}: FieldProps): React.ReactNode {
  const errorId = `${name}-error`;
  return (
    <div>
      <label htmlFor={name} className="mb-1 block text-sm font-medium">
        {label}
        {required ? <span className="ml-0.5 text-emergency">*</span> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        aria-describedby={error ? errorId : undefined}
        className="w-full rounded-md border border-stone bg-paper px-3 py-2 focus:border-bark focus:outline-none"
      />
      {error ? (
        <p id={errorId} className="mt-1 text-sm text-emergency">
          {error}
        </p>
      ) : null}
    </div>
  );
}
